import type React from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { IconButton } from "@similarweb/ui-components";
import { Box, Flex, Stack } from "@similarweb/ui-components/foundations";

import { TRIAL_BANNER_HEIGHT } from "app/components/React/TrialBanner/StyledTrialBanner";
import GradientButton from "app/pages/sales-intelligence/modules/ai-agent/components/GradientButton/GradientButton";
import { useTrackCopyText } from "app/pages/sales-intelligence/modules/ai-agent/hooks/useTrackCopyText";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { AgentKnowledgeBase } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/AgentKnowledgeBase";
import { SuperAgentBreadcrumbs } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/SuperAgentBreadcrumbs/SuperAgentBreadcrumbs";
import { SuperAgentTextarea } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/SuperAgentTextarea/SuperAgentTextarea";
import { AI_AGENT_PRICE_PER_RECORD } from "app/pages/sales-intelligence/modules/quota/constants";
import DataCreditsIndicatorContainer from "app/pages/sales-intelligence/modules/quota/containers/DataCreditsIndicatorContainer/DataCreditsIndicatorContainer";
import { LowQuotaBannerContainer } from "app/pages/sales-intelligence/modules/quota/containers/LowQuotaBannerContainer/LowQuotaBannerContainer";
import { useQuotaGuard } from "app/pages/sales-intelligence/modules/quota/hooks/useQuotaGuard";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";

type Props = {
    value: string;
    setValue: (value: string) => void;
    handleSubmit: () => void;
    isLoading: boolean;
    messagesContainerRef: React.RefObject<HTMLDivElement | null>;
    messagesBottomRef: React.RefObject<HTMLDivElement | null>;
    handleChatReset: () => void;
};

export const SuperAgentChatPageVariant: React.FC<React.PropsWithChildren<Props>> = ({
    children,
    value,
    setValue,
    handleSubmit,
    isLoading,
    messagesContainerRef,
    messagesBottomRef,
    handleChatReset,
}) => {
    const { translate } = useTranslate();
    const isTrialBannerVisible = isTrialOrAfterTrial();

    useTrackCopyText(AIAgentTrackingService.superAgent.trackCopyText);

    const { isBlocked } = useQuotaGuard(AI_AGENT_PRICE_PER_RECORD);

    return (
        <Stack
            margin="0 0 0 189px"
            alignItems="center"
            height={isTrialBannerVisible ? `calc(100vh - ${TRIAL_BANNER_HEIGHT}px)` : "100vh"}
            maxHeight={isTrialBannerVisible ? `calc(100vh - ${TRIAL_BANNER_HEIGHT}px)` : "100vh"}
            overflow="hidden"
            justifyContent="flex-start"
        >
            <SuperAgentBreadcrumbs>
                <Flex
                    margin="0 0 0 auto"
                    gap="8px"
                >
                    <AgentKnowledgeBase position="relative">
                        <DataCreditsIndicatorContainer />
                    </AgentKnowledgeBase>
                    <IconButton
                        iconName="plus"
                        type="flat"
                        iconSize="10px"
                        onClick={handleChatReset}
                    >
                        {translate("si.ai-super-agent.new.chat.button")}
                    </IconButton>
                </Flex>
            </SuperAgentBreadcrumbs>
            <Stack
                padding="0 16px 20px"
                height="calc(100% - 56px)"
            >
                <Box
                    height="100%"
                    overflowY="auto"
                    padding="0 calc((100vw - 724px - 48px) / 2) 20px"
                    ref={messagesContainerRef}
                >
                    <Box padding="24px 16px 0">{children}</Box>
                    <LowQuotaBannerContainer />
                    <div
                        ref={messagesBottomRef}
                        id="super-agent-bottom"
                    />
                </Box>
                <Box padding="0 calc((100vw - 724px - 48px) / 2)">
                    <SuperAgentTextarea
                        value={value}
                        onChange={(value: string) => setValue(value)}
                        placeholder={translate("si.ai-super-agent.textarea.placeholder")}
                        isLoading={isLoading}
                        isDisabled={isBlocked}
                        onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
                            if (event.key === "Enter" && !event.shiftKey && value) {
                                event.preventDefault();
                                handleSubmit();
                            }
                        }}
                        actionButton={
                            <GradientButton
                                variant={value && !isLoading ? "submit" : "disabled"}
                                onClick={handleSubmit}
                                disabled={!value || isLoading}
                            />
                        }
                    />
                </Box>
            </Stack>
        </Stack>
    );
};
